/* Savor + Multi Location Stock Info: inject location-specific strikes. */
(() => {
  const SHOP_DOMAIN = 'lv-ciudvd.myshopify.com';
  const INVENTORY_ENDPOINT = 'https://multiloca.info/api/get-collection-product-inventory';
  const STRIKE_SVG = '<svg viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" class="variant-option__strikethrough" aria-hidden="true" data-location-strikethrough="true"><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"></line><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"></line></svg>';
  let selectedLocationId = null;
  let revision = 0;
  const productCache = new Map();
  const inventoryCache = new Map();

  function normalizeId(value, type) {
    if (value == null) return null;
    if (typeof value === 'object') value = value.locationId ?? value.location_id ?? value.location?.id ?? value.id ?? value.value ?? null;
    if (value == null) return null;
    const raw = String(value).trim();
    const match = raw.match(new RegExp(`gid://shopify/${type}/(\\d+)`, 'i'));
    return match ? match[1] : (/^\d+$/.test(raw) ? raw : null);
  }
  const locationId = (value) => normalizeId(value, 'Location');
  const variantId = (value) => normalizeId(value, 'ProductVariant') ?? String(value ?? '').replace(/\D/g, '');

  function locationFromRadio(radio) {
    if (!radio) return null;
    const row = radio.closest('.location-radio-and-label');
    const container = radio.closest('.location-container');
    const values = [radio.dataset.locationId, radio.getAttribute('data-location-id'), radio.dataset.location, radio.getAttribute('data-location'), radio.value, row?.dataset.locationId, row?.getAttribute('data-location-id'), container?.dataset.locationId, container?.getAttribute('data-location-id')];
    return values.map(locationId).find(Boolean) ?? null;
  }
  function locationFromAppDOM() {
    return [...document.querySelectorAll('#locationNamesContainer input[type="radio"]:checked')].map(locationFromRadio).find(Boolean) ?? null;
  }
  function locationFromStorage() {
    try {
      const raw = localStorage.getItem('selected_location');
      if (!raw) return null;
      try { return locationId(JSON.parse(raw)); } catch { return locationId(raw); }
    } catch { return null; }
  }
  const readCurrentLocation = () => locationFromAppDOM() ?? locationFromStorage();

  function getProductJSON(picker) {
    const handle = picker.dataset.productHandle;
    if (!handle) return Promise.reject(new Error('variant-picker is missing data-product-handle'));
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      productCache.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Product JSON returned ${response.status}`);
        return response.json();
      }));
    }
    return productCache.get(handle);
  }
  function getLocationInventory(productId) {
    const id = String(productId).split('/').pop();
    if (!inventoryCache.has(id)) {
      const url = new URL(INVENTORY_ENDPOINT);
      url.searchParams.set('shopdomain', SHOP_DOMAIN);
      url.searchParams.set('productId', id);
      inventoryCache.set(id, fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Multi Location API ${response.status}`);
        return response.json();
      }).catch((error) => { inventoryCache.delete(id); throw error; }));
    }
    return inventoryCache.get(id);
  }
  function locationInventory(payload, id, location) {
    const map = payload?.inventoryMap ?? {};
    let record = map[variantId(id)];
    if (!record) record = map[Object.keys(map).find((key) => variantId(key) === variantId(id))];
    const level = (record?.inventoryLevels ?? []).find((item) => locationId(item.location_id ?? item.locationId ?? item.location?.id) === location);
    const quantity = Number(level?.available_quantity ?? level?.availableQuantity ?? 0);
    return { assigned: Boolean(level), quantity, available: Boolean(level) && quantity > 0 };
  }

  const optionNames = (product) => (product.options ?? []).map((option) => typeof option === 'string' ? option : option.name);
  function selections(picker, product) {
    return optionNames(product).map((name, index) => {
      const control = [...picker.querySelectorAll('[data-option-name]')].find((node) => node.dataset.optionName === name && ((node instanceof HTMLInputElement && node.checked) || (node instanceof HTMLOptionElement && node.selected)));
      return control?.value ?? product.variants?.[0]?.options?.[index] ?? null;
    });
  }
  function variantForOption(picker, product, input) {
    /*
     * In a single-option picker (this product's Size buttons), Savor already
     * renders the exact Shopify variant id. Use it first. The old matrix-only
     * route is vulnerable during Savor's initial hydration/morph, because the
     * checked option can briefly be stale while the input attributes are not.
     */
    const directVariantId = variantId(input.dataset.variantId);
    if (directVariantId) {
      return (product.variants ?? []).find((variant) => variantId(variant.id) === directVariantId) ?? { id: directVariantId };
    }

    const position = optionNames(product).indexOf(input.dataset.optionName);
    if (position < 0) return null;
    const values = selections(picker, product);
    values[position] = input.value;
    return (product.variants ?? []).find((variant) => variant.options.every((value, index) => String(value) === String(values[index])));
  }

  function ensureStrike(input, shouldStrike) {
    const label = input.closest('.variant-option__button-label');
    if (!label) return;
    const nativeStrike = label.querySelector('.variant-option__strikethrough:not([data-location-strikethrough])');
    const locationStrike = label.querySelector('[data-location-strikethrough]');
    if (nativeStrike || !shouldStrike) locationStrike?.remove();
    else if (!locationStrike) label.insertAdjacentHTML('beforeend', STRIKE_SVG);
  }
  function markInput(input, variant, availability) {
    const available = Boolean(variant && availability.available);
    input.dataset.locationAvailable = String(available);
    input.dataset.locationVariantId = variant ? String(variant.id) : '';
    input.dataset.locationQuantity = String(availability.quantity);
    input.dataset.locationAssigned = String(availability.assigned);
    ensureStrike(input, input.dataset.optionAvailable === 'false' || !available);
  }
  function clearPicker(picker) {
    picker.querySelectorAll('input[data-location-available]').forEach((input) => {
      delete input.dataset.locationAvailable;
      delete input.dataset.locationVariantId;
      delete input.dataset.locationQuantity;
      delete input.dataset.locationAssigned;
    });
    picker.querySelectorAll('[data-location-strikethrough]').forEach((node) => node.remove());
  }
  function setPurchaseState(picker, product, inventory) {
    const form = document.querySelector(`product-form-component[data-product-id="${picker.dataset.productId}"]`);
    if (!form) return;
    const selected = selections(picker, product);
    const current = (product.variants ?? []).find((variant) => variant.options.every((value, index) => String(value) === String(selected[index])));
    const state = current ? locationInventory(inventory, current.id, selectedLocationId) : { available: false };
    const soldOut = !state.available;
    form.dataset.locationVariantState = soldOut ? 'sold-out' : 'in-stock';
    const button = form.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (!button) return;
    button.dataset.locationVariantState = soldOut ? 'sold-out' : 'in-stock';
    button.disabled = soldOut;
    const text = button.querySelector('.add-to-cart-text__content span:last-child');
    if (text) text.textContent = soldOut ? 'SOLD OUT' : 'ADD TO CART';
  }
  async function applyToPicker(picker, expectedRevision) {
    if (!selectedLocationId) { clearPicker(picker); return; }
    try {
      const [product, inventory] = await Promise.all([getProductJSON(picker), getLocationInventory(picker.dataset.productId)]);
      if (expectedRevision !== revision || !picker.isConnected || !selectedLocationId) return;
      [...picker.querySelectorAll('.variant-option__button-label input[type="radio"]')].forEach((input) => {
        const variant = variantForOption(picker, product, input);
        markInput(input, variant, variant ? locationInventory(inventory, variant.id, selectedLocationId) : { assigned: false, quantity: 0, available: false });
      });
      setPurchaseState(picker, product, inventory);
    } catch (error) { console.error('[Location Variants]', error); }
  }
  function applyAll() {
    const current = revision;
    return Promise.all([...document.querySelectorAll('variant-picker[data-product-id]')].map((picker) => applyToPicker(picker, current)));
  }
  function setLocation(id) {
    const normalized = locationId(id);
    if (normalized !== selectedLocationId) { selectedLocationId = normalized; revision += 1; }
    applyAll();
  }

  document.addEventListener('locationChanged', (event) => setLocation(locationId(event.detail?.locationId) ?? readCurrentLocation()));
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('#locationNamesContainer input[type="radio"]')) setLocation(locationFromRadio(target) ?? readCurrentLocation());
  }, true);
  /*
   * Savor 3.5.1 does NOT dispatch shopify:product:select. It morphs the
   * picker and then bubbles its own variant:update event. Repaint after that
   * morph so injected location strikes are not replaced by server HTML.
   */
  document.addEventListener('variant:update', (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker[data-product-id]') : null;
    queueMicrotask(() => {
      if (picker?.isConnected) applyToPicker(picker, revision);
      else applyAll();
    });
  });
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('variant-picker input[type="radio"]')) return;
    /* variant:update is authoritative; this is only a safe fallback. */
    window.setTimeout(applyAll, 0);
  }, true);
  document.addEventListener('shopify:section:load', () => setLocation(readCurrentLocation()));
  function initialize() {
    const initial = readCurrentLocation();
    if (initial) return setLocation(initial);
    applyAll();
    const observer = new MutationObserver(() => {
      const hydrated = readCurrentLocation();
      if (!hydrated) return;
      observer.disconnect();
      setLocation(hydrated);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('locationChanged', () => observer.disconnect(), { once: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  window.locationVariantDebug = () => ({ selectedLocationId, appDOMLocation: locationFromAppDOM(), storageLocation: locationFromStorage(), variants: [...document.querySelectorAll('variant-picker input[type="radio"]')].map((input) => ({ value: input.value, globalAvailable: input.dataset.optionAvailable, locationAvailable: input.dataset.locationAvailable, variantId: input.dataset.locationVariantId || input.dataset.variantId, quantity: input.dataset.locationQuantity, strike: Boolean(input.closest('label')?.querySelector('.variant-option__strikethrough')) })) });
})();
